'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Phone, X, ChevronRight, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import { MegaMenu } from '@/components/Antonie/Components/MegaMenu'
import { motion, AnimatePresence } from 'motion/react'
import { Mask } from '@/components/Mask'
import { Header } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getLink } from '@/utilities/getLink'
import { TypedLocale } from 'payload'
import { LocaleSwitcher } from '@/components/LocaleSwitcher'

export function HeaderClient({
  dark = false,
  antonie = true,
  compact = false,
  navLeft,
  navRight,
  locale,
}: {
  dark?: boolean
  antonie?: boolean
  compact?: boolean
  navLeft: Header['navItems'] | []
  navRight: Header['navItemsRight'] | []
  locale: TypedLocale
}) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [burgerActive, setBurgerActive] = useState(false)
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null)
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)
  const [mobileSubmenu, setMobileSubmenu] = useState<{
    title: string
    description: string
    items: any[]
    image?: string
    link?: any // Added link property
  } | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > (dark ? 10 : 50)) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Animate burger and menu
  useEffect(() => {
    if (mobileMenuOpen) {
      setBurgerActive(true)
    } else {
      setTimeout(() => setBurgerActive(false), 400)
    }
  }, [mobileMenuOpen])

  // Disable page scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [mobileMenuOpen])

  const handleMegaMenuOpen = (linkId: string) => {
    setActiveMegaMenu(linkId)
    setIsMegaMenuOpen(true)
  }

  const handleMegaMenuClose = () => {
    setActiveMegaMenu(null)
    setIsMegaMenuOpen(false)
  }

  const handleMobileSubmenuOpen = (link: any) => {
    if (link.megaMenu) {
      setMobileSubmenu({
        title: link.megaMenu.title ?? '',
        description: link.megaMenu.description ?? '',
        items: link.megaMenu.menuLinks ?? [],
        image: getMediaUrl(link.megaMenu.image) ?? '',
        link: link, // Store the full link object
      })
    }
  }

  const handleMobileSubmenuClose = () => {
    setMobileSubmenu(null)
  }

  // Animation variants for mobile menu
  const menuVariants = {
    open: { x: 0, opacity: 1 },
    closed: { x: '100%', opacity: 0 },
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  }

  const isAntonieStyle = antonie
  const isMenuOpen = mobileMenuOpen || isMegaMenuOpen
  const showCompact = compact || isScrolled
  const useDarkText = compact || (isAntonieStyle && (isMenuOpen || isScrolled))

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isAntonieStyle
          ? showCompact
            ? compact
              ? 'bg-white/95 backdrop-blur-md shadow-sm py-4'
              : 'bg-white/80 backdrop-blur-md shadow-sm py-4'
            : 'bg-transparent py-6'
          : isScrolled
            ? 'bg-white/80 backdrop-blur-md shadow-xs py-4'
            : 'bg-transparent py-6',
      )}
    >
      <div className="mx-auto px-4 md:px-16">
        <div className="flex items-center justify-between md:justify-between">
          <div className="flex items-center justify-start space-x-4 md:hidden">
            <LocaleSwitcher
              className="pt-1"
              variant={isAntonieStyle && !useDarkText ? 'light' : 'default'}
            />
          </div>

          {/* Desktop Navigation - Left */}
          <nav className="hidden items-center space-x-4 xl:flex">
            {navLeft?.map((link, index) => {
              const linkId = `nav-left-${index}`
              return !link.hasMegaMenu ? (
                <div key={link?.link.label + '-' + index}>
                  <Link
                    href={getLink(link.link)}
                    onClick={() => setMobileMenuOpen(false)}
                    onMouseEnter={() => setIsMegaMenuOpen(false)}
                    className={cn(
                      'text-lg font-medium relative z-50 tracking-wide cursor-pointer transition-colors duration-300',
                      isAntonieStyle
                        ? useDarkText
                          ? 'text-gray-800 hover:text-antonie-green'
                          : 'text-white hover:text-antonie-green'
                        : isScrolled
                          ? 'text-gray-700'
                          : dark || isMegaMenuOpen
                            ? 'text-gray-900'
                            : 'text-white',
                      !isAntonieStyle && 'hover:text-antonie-brown',
                    )}
                  >
                    {link.link.label}
                  </Link>
                </div>
              ) : (
                <div key={link?.link.label + '-' + index} className="group relative">
                  <button
                    onClick={() => link.megaMenu && handleMegaMenuOpen(linkId)}
                    className={cn(
                      'text-lg font-medium relative z-50 tracking-wide cursor-pointer transition-colors duration-300',
                      isAntonieStyle
                        ? useDarkText
                          ? 'text-gray-800 hover:text-antonie-green'
                          : 'text-white hover:text-antonie-green'
                        : isScrolled
                          ? 'text-gray-700'
                          : dark || isMegaMenuOpen
                            ? 'text-gray-900'
                            : 'text-white',
                      !isAntonieStyle && 'hover:text-antonie-brown',
                    )}
                  >
                    {link.link.label}
                  </button>
                  {link.megaMenu && isMegaMenuOpen && activeMegaMenu === linkId && (
                    <MegaMenu
                      title={link.megaMenu.title ?? ''}
                      description={link.megaMenu.description ?? ''}
                      items={link.megaMenu.menuLinks ?? []}
                      position="left"
                      image={getMediaUrl(link.megaMenu.image) ?? ''}
                      onClose={handleMegaMenuClose}
                      link={getLink(link.link)}
                    />
                  )}
                </div>
              )
            })}
          </nav>

          {/* Center - Logo */}
          <div
            className={cn(
              'absolute left-1/2 -translate-x-1/2 transform transition-all duration-500 ease-out z-50',
              showCompact || (isAntonieStyle && isMenuOpen) ? 'pt-0' : 'pt-24',
            )}
          >
            <Link
              href={locale === 'de' ? '/' : '/en'}
              className="flex items-center"
              aria-label="Home"
            >
              <Image
                src="/logo_antonie.svg"
                alt="Hotel Logo"
                width={100}
                height={100}
                className={cn(
                  'w-auto transition-all duration-500 ease-out',
                  showCompact || (isAntonieStyle && isMenuOpen)
                    ? useDarkText
                      ? 'h-14 brightness-0'
                      : 'h-14 brightness-0 invert'
                    : isAntonieStyle
                      ? 'h-32 brightness-0 invert lg:h-32 sm:h-24'
                      : dark || isMegaMenuOpen
                        ? 'h-32 brightness-0 lg:h-32 sm:h-24'
                        : 'h-32 brightness-0 invert lg:h-32 sm:h-24',
                )}
              />
            </Link>
          </div>

          {/* Right side - Navigation Links and Buttons */}
          <div className="flex items-center justify-between space-x-6">
            <nav className="hidden items-center space-x-6 xl:flex">
              {navRight?.map((link, index) => {
                const linkId = `nav-right-${index}`
                return !link.hasMegaMenu ? (
                  <div key={link?.link.label + '-' + index}>
                    <Link
                      href={getLink(link.link)}
                      onClick={() => setMobileMenuOpen(false)}
                      onMouseEnter={() => setIsMegaMenuOpen(false)}
                      className={cn(
                        'text-lg font-medium relative z-50 tracking-wide transition-colors duration-300',
                        isAntonieStyle
                          ? useDarkText
                            ? 'text-gray-800 hover:text-antonie-green'
                            : 'text-white hover:text-antonie-green'
                          : isScrolled
                            ? 'text-gray-700'
                            : dark || isMegaMenuOpen
                              ? 'text-gray-900'
                              : 'text-white',
                        !isAntonieStyle && 'hover:text-antonie-brown',
                      )}
                    >
                      {link.link.label}
                    </Link>
                  </div>
                ) : (
                  <div key={link?.link.label + '-' + index} className="group relative">
                    <button
                      onClick={() => link.megaMenu && handleMegaMenuOpen(linkId)}
                      className={cn(
                        'text-lg font-medium relative z-50 tracking-wide transition-colors duration-300',
                        isAntonieStyle
                          ? useDarkText
                            ? 'text-gray-800 hover:text-antonie-green'
                            : 'text-white hover:text-antonie-green'
                          : isScrolled
                            ? 'text-gray-700'
                            : dark || isMegaMenuOpen
                              ? 'text-gray-900'
                              : 'text-white',
                        !isAntonieStyle && 'hover:text-antonie-brown',
                      )}
                    >
                      {link.link.label}
                    </button>
                    {link.megaMenu && isMegaMenuOpen && activeMegaMenu === linkId && (
                      <MegaMenu
                        title={link.megaMenu.title ?? ''}
                        description={link.megaMenu.description ?? ''}
                        items={link.megaMenu.menuLinks ?? []}
                        position="left"
                        image={getMediaUrl(link.megaMenu.image) ?? ''}
                        onClose={handleMegaMenuClose}
                        link={getLink(link.link)}
                      />
                    )}
                  </div>
                )
              })}
            </nav>

            <div className="flex items-center space-x-2">
              <button
                className={cn(
                  'relative z-50 flex h-10 w-10 flex-col items-center justify-center xl:hidden focus:outline-hidden',
                  burgerActive ? 'open' : '',
                )}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                <span
                  className={cn(
                    'block h-0.5 w-7 rounded transition-all duration-300',
                    mobileMenuOpen
                      ? 'rotate-45 translate-y-2 bg-gray-800'
                      : isAntonieStyle
                        ? useDarkText
                          ? 'bg-gray-800'
                          : 'bg-white'
                        : dark || isScrolled
                          ? 'bg-gray-800'
                          : 'bg-white',
                  )}
                />
                <span
                  className={cn(
                    'block h-0.5 w-7 rounded transition-all duration-300 my-1',
                    mobileMenuOpen
                      ? 'opacity-0'
                      : isAntonieStyle
                        ? useDarkText
                          ? 'bg-gray-800'
                          : 'bg-white'
                        : dark || isScrolled
                          ? 'bg-gray-800'
                          : 'bg-white',
                  )}
                />
                <span
                  className={cn(
                    'block h-0.5 w-7 rounded transition-all duration-300',
                    mobileMenuOpen
                      ? '-rotate-45 -translate-y-2 bg-gray-800'
                      : isAntonieStyle
                        ? useDarkText
                          ? 'bg-gray-800'
                          : 'bg-white'
                        : dark || isScrolled
                          ? 'bg-gray-800'
                          : 'bg-white',
                  )}
                />
              </button>
              <div className="flex items-center space-x-1">
                <Link href={locale === 'de' ? '/buchen' : '/book'} aria-label="Jetzt Buchen">
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'hidden xl:flex rounded-none text-sm pointer-events-auto cursor-pointer',
                      isAntonieStyle
                        ? 'border-antonie-red bg-antonie-red text-white hover:bg-antonie-red-hover hover:border-antonie-red-hover hover:text-white'
                        : isScrolled
                          ? 'border-primary text-black bg-primary hover:bg-primary hover:text-black'
                          : dark
                            ? 'border-primary text-black bg-primary hover:bg-primary hover:text-black'
                            : 'border-primary bg-primary text-black hover:bg-primary hover:text-black',
                    )}
                  >
                    {locale === 'de' ? 'Jetzt buchen' : 'Book now'}
                  </Button>
                </Link>
                <Link href={locale === 'de' ? '/kontakt' : '/contact'} aria-label="Anfragen">
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'hidden xl:flex rounded-none text-sm pointer-events-auto cursor-pointer',
                      isAntonieStyle
                        ? 'border-antonie-green bg-antonie-green text-black hover:bg-antonie-green-hover hover:border-antonie-green-hover'
                        : isScrolled
                          ? 'border-primary text-black bg-primary hover:bg-primary hover:text-black'
                          : dark
                            ? 'border-primary text-black bg-primary hover:bg-primary hover:text-black'
                            : 'border-primary bg-primary text-black hover:bg-primary hover:text-black',
                    )}
                  >
                    {locale === 'de' ? 'Anfragen' : 'Contact'}
                  </Button>
                </Link>
                <LocaleSwitcher
                  className={cn(
                    'hidden pt-1 md:flex',
                    isAntonieStyle && !useDarkText && 'hover:!text-antonie-green',
                  )}
                  variant={isAntonieStyle && !useDarkText ? 'light' : 'default'}
                />
                <Link
                  href="tel:+43 5236 203"
                  aria-label="Jetzt anrufen +43 5236 203"
                  className="md:hidden"
                >
                  <button className="relative z-50 cursor-pointer text-lg font-medium tracking-wide transition-colors duration-300">
                    <Phone className="h-8 w-8 text-gray-800" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu with AnimatePresence */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="z-100 fixed inset-0 flex h-screen flex-col items-center justify-center bg-white"
            >
              <button
                className="absolute right-8 top-8 z-50"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-8 w-8 text-gray-800" />
              </button>

              <div className="pointer-events-none absolute left-0 top-0 z-50 flex w-full justify-center">
                <Link href="/" aria-label="Home" className="pointer-events-auto pt-5">
                  <Image
                    src="/logo_antonie.svg"
                    alt="Hotel Logo"
                    width={100}
                    height={100}
                    className={cn('w-auto transition-all duration-300', 'h-12 brightness-0')}
                  />
                </Link>
              </div>

              {/* Mask */}
              <div className="absolute bottom-0 right-0">
                <Mask color="#4B4A4A" width={231} height={123} className="h-full w-full" />
              </div>

              {/* Mobile Menu Content */}
              {!mobileSubmenu ? (
                <nav className="mt-20 flex flex-col items-center space-y-8">
                  {[...(navLeft || []), ...(navRight || [])].map((link: any, idx: number) => (
                    <motion.div
                      key={link.link.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.07 }}
                      className="flex items-center space-x-2"
                    >
                      {link.hasMegaMenu ? (
                        <button
                          onClick={() => handleMobileSubmenuOpen(link)}
                          className="text-2xl font-semibold text-gray-800 transition-colors hover:text-antonie-brown"
                        >
                          {link.link.label}
                        </button>
                      ) : (
                        <Link
                          href={getLink(link.link)}
                          className="text-2xl font-semibold text-gray-800 transition-colors hover:text-antonie-brown"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.link.label}
                        </Link>
                      )}
                      {link.hasMegaMenu && <ChevronRight className="h-5 w-5 text-gray-600" />}
                    </motion.div>
                  ))}
                </nav>
              ) : (
                /* Mobile Submenu Content */
                <div
                  className={cn(
                    showCompact ? 'mt-20' : 'mt-40',
                    'flex h-full w-full flex-col items-center justify-start px-6',
                  )}
                >
                  <div className="mb-8 flex w-full items-center justify-between">
                    <button
                      onClick={handleMobileSubmenuClose}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                    >
                      <ArrowLeft className="h-5 w-5" />
                      <span className="text-lg">Back</span>
                    </button>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex h-full w-full max-w-md flex-col"
                  >
                    <div className="mb-6 text-center">
                      <Link
                        href={getLink(mobileSubmenu.link)}
                        className="block"
                        onClick={() => {
                          setMobileSubmenu(null)
                          setMobileMenuOpen(false)
                        }}
                      >
                        <h2 className="mb-2 text-2xl font-semibold text-gray-900 transition-colors hover:text-antonie-brown">
                          {mobileSubmenu.title}
                        </h2>
                      </Link>
                      <p className="hidden text-gray-600 md:block">{mobileSubmenu.description}</p>
                    </div>

                    <div
                      className="flex-1 space-y-4 overflow-y-auto pb-20"
                      style={{ maxHeight: 'calc(100vh - 300px)' }}
                    >
                      {mobileSubmenu.items.map((item: any, index: number) => (
                        <motion.div
                          key={item.name + '-' + index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Link
                            href={getLink(item.link)}
                            className="block rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50"
                            onClick={() => {
                              setMobileSubmenu(null)
                              setMobileMenuOpen(false)
                            }}
                          >
                            <h3 className="mb-1 text-lg font-medium text-gray-900">
                              {item.link.label}
                            </h3>
                            {item.descriptionLink && (
                              <p className="text-sm text-gray-600">{item.descriptionLink}</p>
                            )}
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              )}
            </motion.div>
            {/* Overlay */}
            <motion.div
              className="z-99 fixed inset-0 bg-black bg-opacity-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          </>
        )}
      </AnimatePresence>
    </header>
  )
}
